import Big, { BigSource } from "big.js";
import omitBy from "lodash/omitBy";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemCalculatedValues,
	OrderItemInputLike,
	OrderItemOptionCalculatedValues,
	OrderItemOptionInputLike,
	OrdersGroupValueLike,
	SubtotalAddonsLike,
} from "../types";
import { DISCOUNT_TYPE } from "~served/configs/enums";

export const BASE_SUBTOTAL_ADDONS: SubtotalAddonsLike = {
	offer: {
		amount: 0,
		is_custom_value: false,
	},
	discount: {
		is_divided: false,
		type: DISCOUNT_TYPE.PERCENTAGE,
		value: 0,
		amount: 0,
	},
	vat: {
		is_included: false,
		percentage: 0,
		amount: 0,
	},
	service_charge: {
		is_included: false,
		percentage: 0,
		amount: 0,
	},
	service_charge_bd: {
		is_included: false,
		percentage: 0,
		amount: 0,
	},
	adjustment: {
		amount: 0,
	},
	card_surcharge: {
		percentage: 0,
		amount: 0,
	},
	tax: {
		percentage: 0,
		amount: 0,
	},
};

export const BASE_ORDERS_GROUP_VALUE: OrdersGroupValueLike = {
	$orders: [],
	$isAllItemsWithVat: true,
	$isSomeItemsWithVat: false,
	$isAllItemsWithServiceCharge: true,
	$isAllItemsWithServiceChargeBd: true,
	$isSomeItemsWithServiceCharge: false,
	$isSomeItemsWithServiceChargeBd: false,
	$isAllItemsWithCardSurcharge: true,
	$isSomeItemsWithCardSurcharge: false,
	$isAllItemsWithTax: true,
	$isSomeItemsWithTax: false,
	$roundedGrandTotal: 0,
	items: [],
	cancelled_items: [],
	max_prepare_time: 0,
	items_count: 0,
	items_count_by_types: {},
	prepped_count: 0,
	subtotal: 0,
	discount_amount: 0,
	offer_amount: 0,
	net_amount: 0,
	vat_amount: 0,
	service_charge_amount: 0,
	service_charge_bd_amount: 0,
	tax_amount: 0,
	card_surcharge_amount: 0,
	adjustment_amount: 0,
	grand_total: 0,
	gross_amount: 0,
	cancelled_amount: 0,
};

export const sortByAscendingSubtotal = <T extends { subtotal: number }>(
	a: T,
	b: T,
) => a.subtotal - b.subtotal;

export const $deepPopulateItemMetadata = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	orderItem,
): OrderItemInputLike<
	OrderItemCalculatedValues<TOrderItem>,
	TOrderItemOption & OrderItemOptionCalculatedValues
> => {
	const updatedItem = $getItemMetadata(orderItem);
	return {
		...updatedItem,
		options: updatedItem.options.map((orderItemOption) =>
			$getOptionMetadata(orderItemOption, updatedItem),
		),
	};
};

export const $getOptionServingQuantity = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	option: OrderItemOptionInputLike<TOrderItemOption>,
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) => bigMath.mul(option.quantity, item.quantity);

export const $getOptionSubtotal = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	option: OrderItemOptionInputLike<TOrderItemOption>,
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) => bigMath.mul(option.listed_price, $getOptionServingQuantity(option, item));

export const $getOptionMetadata = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	option: OrderItemOptionInputLike<TOrderItemOption>,
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
): OrderItemOptionCalculatedValues<
	OrderItemOptionInputLike<TOrderItemOption>
> => ({
	net_amount: 0,
	gross_amount: 0,
	cancelled_amount: 0,
	...option,
	serving_quantity: $getOptionServingQuantity(option, item),
	subtotal: $getOptionSubtotal(option, item),
	subtotal_addons: {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(option.subtotal_addons, (value) => value === null),
	},
});

export const $getItemServingQuantity = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) =>
	bigMath.add(item.quantity, bigMath.mul(item.quantity, item.extra_quantity));

export const $getItemMetadata = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
): OrderItemCalculatedValues<
	OrderItemInputLike<TOrderItem, TOrderItemOption>
> => ({
	net_amount: 0,
	gross_amount: 0,
	cancelled_amount: 0,
	...item,
	serving_quantity: $getItemServingQuantity(item),
	unit_price: $getItemUnitPrice(item),
	subtotal: $getItemSubtotal(item),
	subtotal_addons: {
		...BASE_SUBTOTAL_ADDONS,
		...omitBy(item.subtotal_addons, (value) => value === null),
	},
});

export const $isOrderUnpaid = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>,
) => !order.is_paid && !order.is_cancelled;

export const toRoundedNumber = (num: BigSource) =>
	new Big(num).toNumber() < 0.5
		? new Big(num).toNumber()
		: new Big(num).round().toNumber();

export const $getItemSubtotal = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) => {
	return new Big($getItemUnitPrice(item))
		.times(item.quantity || 0)
		.round(2)
		.toNumber();
};

export const $getItemUnitPrice = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) => {
	return new Big(item.listed_price || 0)
		.add(
			item.options.reduce<BigSource>(
				(pre, cur) =>
					new Big(pre).add(
						new Big(cur.listed_price || 0).mul(cur.quantity || 1),
					),
				0,
			),
		)
		.round(2)
		.toNumber();
};

export const $getItemTotalRawPriceAddons = <
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	item: OrderItemInputLike<TOrderItem, TOrderItemOption>,
) => {
	return new Big(item.original_price_addons.vat.amount)
		.add(item.original_price_addons.service_charge.amount)
		.add(item.original_price_addons.service_charge_bd?.amount ?? 0)
		.add(
			item.options.reduce<BigSource>(
				(pre, cur) =>
					new Big(pre)
						.add(cur.original_price_addons.vat.amount)
						.add(cur.original_price_addons.service_charge.amount)
						.add(cur.original_price_addons.service_charge_bd?.amount ?? 0),
				0,
			),
		)
		.times(item.quantity)
		.round(2)
		.toNumber();
};

export const $getOptionTotalRawPriceAddons = <
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>(
	option: OrderItemOptionInputLike<TOrderItemOption>,
) => {
	return new Big(option.original_price_addons.vat.amount)
		.add(option.original_price_addons.service_charge.amount)
		.add(option.original_price_addons.service_charge_bd?.amount ?? 0)
		.times(option.serving_quantity)
		.round(2)
		.toNumber();
};

export function $mergeObj(a, b) {
	const merged = { ...a };

	for (const key in b) {
		if (key in merged) {
			merged[key] += b[key];
		} else {
			merged[key] = b[key];
		}
	}
	return merged;
}
